/**
 * Mini-Project goal "primitive" ↔ unified mission_type_key bridge
 * (DEV-1075 Phase 7.1).
 *
 * The MP UI exposes a 4-value "primitive" picker (count, percentage,
 * checklist, currency) when a Lead adds a goal. The primitive is the
 * simplest mental model for a grassroots Lead: "what kind of number
 * am I tracking?"
 *
 * Underneath, the unified kpi_config table (post-DEV-1075) stores
 * the FP-native mission_type_key + value_type + uom triple — those
 * are the fields the engine and rest of the platform dispatch on.
 * MP-side rendering needs to round-trip primitive ↔ that triple, so
 * this module is the single source of truth for the bridge.
 *
 * The same mapping is hard-coded in the Phase 3 backfill SQL
 * (migrations/178_backfill_mp_goals_to_unified.sql §1) — keep them
 * in sync. Phase 9 cleanup will add a drift check.
 *
 * Why no "primitive" column on kpi_config? Primitives are an MP-UI
 * concept that doesn't exist for FP — FP dispatches directly on
 * mission_type_key with no equivalent abstraction layer. Adding a
 * column would either be MP-only nullable noise on FP rows, or would
 * force FP code to ignore an irrelevant field. The inverse heuristic
 * below recovers the primitive from the type triple cheaply.
 */

export type MpGoalPrimitive = 'count' | 'percentage' | 'checklist' | 'currency';

export const MP_GOAL_PRIMITIVES: ReadonlyArray<MpGoalPrimitive> = [
  'count',
  'percentage',
  'checklist',
  'currency',
] as const;

/**
 * Configuration for a single primitive. Drives the AddGoal form's
 * defaults when the user picks this primitive.
 */
export interface MpPrimitiveSpec {
  /** Maps onto kpi_config.mission_type_key. */
  missionTypeKey: 'count' | 'rate' | 'milestone_set';
  /** Maps onto kpi_config.value_type. */
  valueType: 'numeric' | 'percentage' | 'milestone_set';
  /** Default unit-of-measure for kpi_config.uom. Null = let the user fill
   *  it in or leave empty (count of trees, students, etc.). For currency,
   *  the actual currency code (USD, EUR, …) goes into uom at write time. */
  defaultUom: string | null;
  /** True when the AddGoal form should show a currency-code field
   *  (instead of a freeform unit-key field). */
  needsCurrencyCode: boolean;
  /** True when the AddGoal form should show the checklist-items editor. */
  needsChecklistItems: boolean;
}

/**
 * Forward map: primitive → unified type triple.
 * Used when CREATING a new goal from the MP UI.
 */
export const MP_PRIMITIVE_TO_MISSION_TYPE: Record<MpGoalPrimitive, MpPrimitiveSpec> = {
  count: {
    missionTypeKey: 'count',
    valueType: 'numeric',
    defaultUom: null,
    needsCurrencyCode: false,
    needsChecklistItems: false,
  },
  percentage: {
    missionTypeKey: 'rate',
    valueType: 'percentage',
    defaultUom: '%',
    needsCurrencyCode: false,
    needsChecklistItems: false,
  },
  checklist: {
    missionTypeKey: 'milestone_set',
    valueType: 'milestone_set',
    defaultUom: null,
    needsCurrencyCode: false,
    needsChecklistItems: true,
  },
  currency: {
    // Currency is "count of money" — same engine path as count, but the
    // UI surfaces a currency-code picker instead of a unit field, and
    // formats values with currency symbols.
    missionTypeKey: 'count',
    valueType: 'numeric',
    defaultUom: null, // overwritten by the user's currency_code at write time
    needsCurrencyCode: true,
    needsChecklistItems: false,
  },
};

/**
 * Inverse heuristic: derive the MP primitive from the unified type
 * triple. Used when RENDERING a goal in the MP UI from a unified
 * KpiConfigResponse.
 *
 * Rules:
 *   mission_type_key === 'rate'           → 'percentage'
 *   mission_type_key === 'milestone_set'  → 'checklist'
 *   mission_type_key === 'count' AND
 *     uom matches a 3-letter ISO 4217-shaped code → 'currency'
 *   otherwise                              → 'count'
 *
 * The currency tiebreaker assumes a Lead doesn't type "USD" as the
 * unit for a count-of-things goal (they type "trees", "students",
 * etc.). Practical ~99% truth in the MP context. If a real
 * collision surfaces, the fix is a `primitive` column on kpi_config —
 * out of Phase 7.1 scope.
 */
export function inferPrimitiveFromUnified(
  missionTypeKey: string | null | undefined,
  uom: string | null | undefined,
): MpGoalPrimitive {
  if (missionTypeKey === 'rate') return 'percentage';
  if (missionTypeKey === 'milestone_set') return 'checklist';
  if (
    missionTypeKey === 'count'
    && typeof uom === 'string'
    && /^[A-Z]{3}$/.test(uom)
  ) {
    return 'currency';
  }
  return 'count';
}
